import type { PointerEvent as ReactPointerEvent, RefObject } from "react";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import type { SetState } from "../../vendor/src/features/remotion-editor/editor/context-provider";
import { getCompositionDuration } from "../../vendor/src/features/remotion-editor/editor/utils/get-composition-duration";
import { getVisibleFrames } from "../../vendor/src/features/remotion-editor/editor/utils/get-visible-frames";
import { timelineScrollContainerRef } from "../../vendor/src/features/remotion-editor/editor/utils/restore-scroll-after-zoom";
import { getOffsetOfTrack } from "../../vendor/src/features/remotion-editor/editor/utils/position-utils";
import { getTrackHeight } from "../../vendor/src/features/remotion-editor/editor/state/items";
import { collectSnapPoints } from "../../vendor/src/features/remotion-editor/editor/timeline/utils/snap-points";
import {
  forceSpecificCursor,
  stopForcingSpecificCursor,
} from "../../vendor/src/features/remotion-editor/editor/force-specific-cursor";
import { generateRandomId } from "../../vendor/src/features/remotion-editor/editor/utils/generate-random-id";
import {
  dropTimelineSelection,
  pointerDropFrame,
  type DropTarget,
} from "./timeline-drop";
import { itemOwner } from "./group-split";
import { trackKind } from "./track-layout";
import { snapMoveEdges } from "./move-snapping";
import type { useDragOverlay } from "../../vendor/src/features/remotion-editor/editor/drag-overlay-provider";

const sessions = new WeakSet<object>();
const completedClicks = new WeakMap<object, number>();
export const consumeMoveClick = (key: object, time: number) => {
  const end = completedClicks.get(key);
  completedClicks.delete(key);
  return end !== undefined && time >= end && time - end < 100;
};

/** Window-owned gesture: changing tracks can unmount the initiating bar without cancelling the move. */
export function startPointerMove({
  event,
  stateRef,
  setState,
  itemId,
  ids,
  timelineWidth,
  overlay,
}: {
  event: Pick<
    ReactPointerEvent<HTMLDivElement>,
    "clientX" | "clientY" | "pointerId" | "preventDefault"
  >;
  stateRef: RefObject<EditorState>;
  setState: SetState;
  itemId: string;
  ids: string[];
  timelineWidth: number;
  overlay: Pick<
    ReturnType<typeof useDragOverlay>,
    "startDrag" | "updateCursorPosition" | "stopDrag" | "setSnappedPositions"
  >;
}): void {
  const original = stateRef.current,
    owner = itemOwner(original, itemId),
    primary = original.undoableState.items[itemId];
  const candidateContainer = timelineScrollContainerRef.current;
  if (
    !owner ||
    !primary ||
    owner.track.locked ||
    !candidateContainer ||
    timelineWidth <= 0 ||
    sessions.has(stateRef)
  )
    return;
  const container = candidateContainer;
  event.preventDefault();
  sessions.add(stateRef);
  const base = {
    ...original,
    selectedItems: ids,
    activeSelectionSurface: "timeline" as const,
  };
  const token = "move-" + generateRandomId(16),
    pointerId = event.pointerId,
    startX = event.clientX,
    startY = event.clientY;
  const scrollX = container.scrollLeft,
    scrollY = container.scrollTop;
  const visible = getVisibleFrames({
    fps: base.undoableState.fps,
    totalDurationInFrames: getCompositionDuration(
      base.undoableState.items,
      base.undoableState.tracks,
    ),
  });
  const rows = base.undoableState.tracks.map((track, index) => ({
    track,
    index,
    top: getOffsetOfTrack({ trackIndex: index, ...base.undoableState }),
    height: getTrackHeight({ track, items: base.undoableState.items }),
  }));
  const row = rows.find((r) => r.track.id === owner.track.id)!;
  const snaps = collectSnapPoints({
    tracks: base.undoableState.tracks,
    items: base.undoableState.items,
    excludeItemIds: ids,
  });
  let started = false,
    ended = false,
    raf = 0,
    lastKey = "",
    pending: PointerEvent | null = null;
  let lastPlan = dropTimelineSelection(base, ids, itemId, primary.from, {
    trackId: owner.track.id,
  });
  let lastInput: EditorState | null = null,
    established = false;
  const owns = (current: EditorState) =>
    current === lastInput ||
    current.documentMutationOwner === token ||
    (!established && current.undoableState === original.undoableState);
  const cleanup = () => {
    sessions.delete(stateRef);
    cancelAnimationFrame(raf);
    window.removeEventListener("pointermove", move);
    window.removeEventListener("pointerup", up);
    window.removeEventListener("pointercancel", cancel);
    window.removeEventListener("blur", cancel);
    window.removeEventListener("keydown", key, true);
    stopForcingSpecificCursor();
    overlay.stopDrag();
  };
  const finish = (rollback: boolean, conflict = false, sync = false) => {
    if (ended) return;
    ended = true;
    cleanup();
    if (!started) {
      if (rollback && !lastPlan.ok)
        toast.info("같은 종류의 빈 트랙이나 트랙 사이에 배치해 주세요.");
      return;
    }
    const publish = () =>
      setState({
        mutationOwner: token,
        update: (current) => {
          if (!owns(current))
            return current.timelineMoveActive
              ? { ...current, timelineMoveActive: false, activeSnapPoint: null }
              : current;
          return {
            ...current,
            undoableState: rollback
              ? original.undoableState
              : current.undoableState,
            selectedItems: rollback
              ? base.selectedItems
              : current.selectedItems,
            timelineMoveActive: false,
            activeSnapPoint: null,
            timelineTimeEditApplied: true,
          };
        },
        commitToUndoStack:
          (!rollback &&
            lastPlan.ok &&
            lastPlan.state.undoableState !== original.undoableState) ||
          conflict,
      });
    if (sync) flushSync(publish);
    else publish();
    if (conflict)
      toast.info(
        "다른 편집이 적용되어 이동을 멈췄습니다. 현재 상태에서 다시 조정해 주세요.",
      );
    else if (rollback && !lastPlan.ok)
      toast.info(
        "이 위치에는 배치할 수 없습니다. 같은 종류의 빈 트랙이나 트랙 사이로 옮겨 주세요.",
      );
  };
  const render = (pointer: PointerEvent) => {
    if (!owns(stateRef.current)) {
      finish(true, true);
      return;
    }
    const dx = pointer.clientX - startX + container.scrollLeft - scrollX,
      dy = pointer.clientY - startY + container.scrollTop - scrollY;
    if (!started && Math.hypot(dx, dy) < 3) return;
    const y = row.top + row.height / 2 + dy;
    let target: DropTarget = { trackId: owner.track.id };
    if (Math.abs(dy) > 4) {
      const gap = rows.find((r) => r.index > 0 && Math.abs(y - r.top) < 3);
      const end = rows.at(-1)!.top + rows.at(-1)!.height;
      if (gap || y < 0 || y > end) {
        target = {
          newTrack: {
            id: token + "-track",
            index: gap?.index ?? (y < 0 ? 0 : rows.length),
            kind: trackKind(owner.track),
          },
        };
      } else {
        const hit = rows.find((r) => y >= r.top && y < r.top + r.height);
        if (hit) target = { trackId: hit.track.id };
      }
    }
    const raw = Math.max(
      0,
      primary.from + Math.round((dx / timelineWidth) * visible),
    );
    const snapped =
      base.isSnappingEnabled && !pointer.altKey
        ? snapMoveEdges(
            raw,
            primary.durationInFrames,
            snaps,
            (6 / timelineWidth) * visible,
          )
        : { from: raw, point: null };
    const snap = snapped.point;
    const frame = pointerDropFrame(base, ids, itemId, snapped.from, target);
    const cache = `${frame}:${"trackId" in target ? target.trackId : target.newTrack.index}:${snap?.frame ?? ""}:${snapped.from}`;
    if (started) overlay.updateCursorPosition(pointer.clientX, pointer.clientY);
    if (cache === lastKey) return;
    lastKey = cache;
    lastPlan = dropTimelineSelection(base, ids, itemId, frame, target);
    forceSpecificCursor(lastPlan.ok ? "grabbing" : "not-allowed");
    if (!started) {
      overlay.startDrag({
        itemIds: ids,
        clickedItemId: itemId,
        timelineWidth,
        visibleFrames: visible,
        clickX: startX,
        clickY: startY,
        tracks: base.undoableState.tracks,
        items: base.undoableState.items,
      });
      overlay.updateCursorPosition(pointer.clientX, pointer.clientY);
      setState({ update: (current) => current, commitToUndoStack: true });
      started = true;
    }
    const planned = lastPlan.ok ? lastPlan.state : base;
    overlay.setSnappedPositions(
      snap
        ? Object.fromEntries(
            ids
              .filter((id) => base.undoableState.items[id])
              .map((id) => [
                id,
                base.undoableState.items[id]!.from +
                  snapped.from -
                  primary.from,
              ]),
          )
        : null,
    );
    setState({
      mutationOwner: token,
      update: (current) => {
        if (!owns(current)) return current;
        lastInput = current;
        established = true;
        return {
          ...current,
          // A snapped no-op still owns a live drag. External document edits and Undo clear this stamp.
          documentMutationOwner: token,
          undoableState: planned.undoableState,
          selectedItems: planned.selectedItems,
          activeSelectionSurface: "timeline",
          timelineMoveActive: true,
          activeSnapPoint:
            snap && lastPlan.ok
              ? {
                  ...snap,
                  frame:
                    snap.frame +
                    (planned.undoableState.items[itemId]?.from ?? frame) -
                    snapped.from,
                }
              : null,
          timelineTimeEditApplied: true,
        };
      },
      commitToUndoStack: false,
    });
  };
  function tick() {
    if (ended) return;
    if (!container.isConnected) {
      finish(true);
      return;
    }
    if (!owns(stateRef.current)) {
      finish(true, true);
      return;
    }
    if (pending) {
      // The native drag-overlay provider owns edge scrolling; do not apply a second scroll delta.
      render(pending);
    }
    if (!ended) raf = requestAnimationFrame(tick);
  }
  function move(pointer: PointerEvent) {
    if (pointer.pointerId !== pointerId) return;
    pending = pointer;
  }
  function up(pointer: PointerEvent) {
    if (pointer.pointerId !== pointerId) return;
    render(pointer);
    if (started) completedClicks.set(stateRef, pointer.timeStamp);
    finish(!lastPlan.ok, false, true);
  }
  function cancel() {
    finish(true, false, true);
  }
  function key(e: KeyboardEvent) {
    if (
      e.code === "Escape" ||
      (e.code === "KeyZ" && (e.metaKey || e.ctrlKey))
    ) {
      e.preventDefault();
      e.stopPropagation();
      finish(true, false, true);
    }
  }
  window.addEventListener("pointermove", move);
  window.addEventListener("pointerup", up);
  window.addEventListener("pointercancel", cancel);
  window.addEventListener("blur", cancel);
  window.addEventListener("keydown", key, true);
  raf = requestAnimationFrame(tick);
}
